import { DocumentClassAttributes, MaterialAttributes, OrderDetailAttributes, SubRouteAttributes, WarehouseAttributes } from '@albatrosdeveloper/ave-models-package';
import { CommonModule, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavContainer, MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatMenuModule } from '@angular/material/menu';
import { CompanyService } from 'app/modules/components/configuration/company/company.service.service';
import { WarehouseService } from 'app/modules/components/configuration/warehouse/warehouse.service';
import { SubRouteService } from 'app/modules/components/routing/sub-routes/sub-route.service';
import { getCode } from 'app/utils/method';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { debounceTime, distinctUntilChanged, Subject, Subscription, skip, take, takeUntil } from 'rxjs';
import { OrderCustomerService } from '../../historic/order-customer/order-customer.service';
import { IPaginationResult, IRequestResult } from 'app/common/interfaces';
import { MaterialService } from 'app/modules/components/material/material/material.service';
import { OrdersListService } from 'app/modules/components/sales/order-list-components/order-list.service';
import { MasterUserInterface } from 'app/modules/components/sales/order-generate/interfaces/master-user.interface';
import { environment } from 'environments/environment.prod';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { SearchService } from 'app/layout/common/search-page/search-page.service';
import { FilterComponent } from 'app/common/components/filter/filter.component';
import { FilterSectionComponent } from 'app/common/components/filter-section/filter-section.component';
import { IFilters } from 'app/modules/components/filters/interfaces/filterInterface';
import { BusinessPartnersFiltersComponent } from 'app/modules/components/filters/business-partners-filters/business-partners-filters.component';
import { ConfigurationFiltersComponent } from 'app/modules/components/filters/configuration-filters/configuration-filters.component';
import { DatesFiltersComponent } from 'app/modules/components/filters/dates-filters/dates-filters.component';
import { DocumentsFiltersComponent } from 'app/modules/components/filters/documents-filters/documents-filters.component';
import { MaterialsManagementFiltersComponent } from 'app/modules/components/filters/materials-management-filters/materials-management-filters.component';
import { RoutesFiltersComponent } from 'app/modules/components/filters/routes-filters/routes-filters.component';
import { StatusFiltersComponent } from 'app/modules/components/filters/status-filters/status-filters.component';
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";
import { DateHelper } from 'app/utils/date.helper';

// Smart Grid imports
import { SmartGridComponent } from 'app/shared/components/smart-grid/components/smart-grid/smart-grid.component';
import { ColDef } from 'app/shared/components/smart-grid/core/interfaces/col-def.interface';
import { GridConfig } from 'app/shared/components/smart-grid/core/interfaces/grid-config.interface';
import { PageChangedEvent } from 'app/shared/components/smart-grid/core/interfaces/pagination-state.interface';
import { SortChangedEvent } from 'app/shared/components/smart-grid/core/interfaces/sort-state.interface';
import { SearchChangedEvent } from 'app/shared/components/smart-grid/core/interfaces/grid-events.interface';

// Cell Renderers
import { RouteUserCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/route-user-cell-renderer/route-user-cell-renderer.component';
import { CustomerCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/customer-cell-renderer/customer-cell-renderer.component';
import { MaterialCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/material-cell-renderer/material-cell-renderer.component';
import { QuantityCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/quantity-cell-renderer/quantity-cell-renderer.component';
import { DateHourCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/date-hour-cell-renderer/date-hour-cell-renderer.component';
import { DecimalCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/decimal-cell-renderer/decimal-cell-renderer.component';
import { CurrencyCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/currency-cell-renderer/currency-cell-renderer.component';
import { ActionsCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/actions-cell-renderer/actions-cell-renderer.component';
import { SerialNavigationCellRendererComponent } from './cell-renderers/serial-navigation-cell-renderer.component';
import { CompanyCurrencyPipe } from 'app/pipes/currency.pipe';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { CustomMatPaginatorIntl } from 'app/mock-api/common/customPaginatorIntl';
import { DocumentTypeEnum } from 'app/common/enums/document-types.enum';
import { BusinessPartnerCellRendererComponent } from 'app/modules/components/monitor/monitor-routes/cell-renderers/business-partner-cell-renderer.component';


@Component({
  selector: "app-request-change",
  standalone: true,
  imports: [
    CommonModule,
    MatSidenavContainer,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatSidenavModule,
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatDatepickerModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
    MatAutocompleteModule,
    FilterComponent,
    FilterSectionComponent,
    StatusFiltersComponent,
    MaterialsManagementFiltersComponent,
    DatesFiltersComponent,
    ConfigurationFiltersComponent,
    DocumentsFiltersComponent,
    RoutesFiltersComponent,
    BusinessPartnersFiltersComponent,
    HoverMenuDirective,
    CompanyCurrencyPipe,
    SmartGridComponent,
  ],
  providers: [
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
    DecimalPipe,
  ],
  templateUrl: "./request-change.component.html",
  styleUrl: "./request-change.component.scss",
})
export class RequestChangeComponent implements OnDestroy {
  getCode = getCode;
  private isInitialized = false;
  type = DocumentTypeEnum.SOLICITUD_CAMBIO_PRODUCTO; //Cambio

  // Smart Grid Configuration
  columnDefs: ColDef<any>[] = [
    {
      field: "serials",
      headerName: "Nro. Doc.",
      cellRenderer: SerialNavigationCellRendererComponent,
      cellRendererParams: {
        documentType: this.type, // DT009
      },
      minWidth: 160,
      sortable: true,
    },
    {
      colId: "routeUser",
      field: "subRoute.name", // Field used for sorting
      pinned: "left",
      headerName: "Ruta - Usuario",
      cellRenderer: RouteUserCellRendererComponent,
      minWidth: 300,
      sortable: true, // Enabled sorting by subRoute.name
    },
    {
      field: "date",
      headerName: "Fecha",
      cellRenderer: DateHourCellRendererComponent,
      minWidth: 180,
      sortable: true,
    },
    {
      field: "shipToExternalKeyCode",
      headerName: "Pagador/Receptor",
      cellRenderer: BusinessPartnerCellRendererComponent,
      autoSize: true,
      minWidth: 250, // Much wider for code + long name
      sortable: true,
      filterOptions: {
        // Display customer name in filter dropdown instead of code
        // Note: Requires shipToName field to be present in row data
        valueExtractor: (item: any) => item?.soldTo?.name,
        customFilterFn(rowData, filterValue) {
          const rowCustomer = rowData?.soldTo?.name;
          if (!rowCustomer) return !filterValue;

          return rowCustomer === filterValue;
        },
      },
    },
    {
      field: "materialName",
      headerName: "Material",
      cellRenderer: MaterialCellRendererComponent,
      minWidth: 300,
      sortable: true,
    },
    {
      field: <any>"returnType.name",
      headerName: "Motivo de Cambio",
      minWidth: 150,
      sortable: false,
    },
    {
      field: "soldQuantity",
      headerName: "Cantidad",
      cellRenderer: QuantityCellRendererComponent,
      cellRendererParams: {
        getUMPActive: () => this.UMPActive,
      },
      minWidth: 180,
      sortable: true,
    },
    {
      field: "unitPrice",
      headerName: "Precio",
      cellRenderer: CurrencyCellRendererComponent,
      minWidth: 120,
      sortable: true,
      cellClass: "text-right",
    },
    {
      field: "subTotal",
      headerName: "Subtotal",
      cellRenderer: CurrencyCellRendererComponent,
      minWidth: 120,
      sortable: true,
      cellClass: "text-right",
    },
    {
      field: "total",
      headerName: "Total",
      cellRenderer: CurrencyCellRendererComponent,
      minWidth: 120,
      sortable: true,
      cellClass: "text-right",
    },
    {
      colId: "actions",
      headerName: "Acciones",
      cellRenderer: ActionsCellRendererComponent,
      cellClass: "text-center",
      headerClass: "text-center",
      minWidth: 80,
      width: 80,
      sortable: false,
      pinned: "right",
    },
  ];

  gridConfig: GridConfig<any> = {
    rowIdField: "_id" as any,
    emptyState: {
      message: "No encontramos tus resultados!",
      showIcon: true,
    },
    pagination: {
      enabled: true,
      mode: "api",
      pageSize: 50,
      pageSizeOptions: [50, 100, 150, 200],
      showPaginator: true,
    },
    sorting: {
      enabled: true,
      mode: "api",
      multiColumn: false,
      defaultSort: [{ field: "total" as any, direction: "desc" }],
    },
    search: {
      enabled: false,
    },
    columnFilters: {
      enabled: true,
      mode: "local",
    },
    columnVisibility: {
      enabled: false, // Disabled - now in settings panel
    },
    selection: {
      enabled: false,
    },
    autoSizing: {
      enabled: true,
      includeHeaders: true,
      skipHidden: true,
      padding: 40, // Increased for more spacing
    },
    styling: {
      maxHeight: "85vh",
      minHeight: "80vh",
      density: "comfortable",
      fontSize: "12px",
      cellPaddingX: "3px",
      cellPaddingY: "3px",
    },
    settingsPanel: {
      enabled: true,
      position: "right",
      enableColumnVisibility: true,
      defaultDensity: "compact",
    },
  };

  // Data
  data: OrderDetailAttributes[] = [];
  totalItems: number = 0;
  loading: boolean = false;

  // Pagination & Sorting
  searchControl = new FormControl("");
  search: string = "";
  pageSize: number = 50;
  pageIndex: number = 1;
  sortActive: keyof OrderDetailAttributes = "total";
  sortDirection: "ASC" | "DESC" = "DESC";

  filter = {
    company: null,
    dateini: new Date(),
    dateend: new Date(),
    warehouseId: null,
    routes: [],
    item: null,
  };

  range = new FormGroup({
    start: new FormControl<Date | null>(new Date()),
    end: new FormControl<Date | null>(new Date()),
  });

  warehouseList: WarehouseAttributes[] = [];
  warehouseFilterList: WarehouseAttributes[] = [];
  searchWarehouse = "";

  subRouteList: SubRouteAttributes[] = [];
  listFilterSubRouteList: SubRouteAttributes[] = [];
  searchSubRoute = "";

  UMPActive: boolean = false;
  currencyCode: string = "";

  materialList: MaterialAttributes[] = [];
  searchMaterial = "";
  private searchSubject = new Subject<string>();

  // selectedDocumentClass: string = '0';
  selectedDocumentClass: string[] = [];
  filteredDocumentClasses: Subject<DocumentClassAttributes[]> = new Subject();
  documentClassMultiFilterCtrl: FormControl = new FormControl();
  isIndeterminateDocumentClass: boolean = false;
  isCheckedDocumentClass: boolean = false;
  private _onDestroyDocumentClass = new Subject<void>();
  private _onDestroy = new Subject<void>();

  selectedSeller: string = "0";
  selectedWithTravel: string[] = [];

  documentClassesList: DocumentClassAttributes[] = [];
  sellerList: MasterUserInterface[] = [];

  selectedSoldTo: string = "0";
  soldToControl = new FormControl("");
  soldToList: any[] = [];
  soldToFilterSelected: any = null;

  selectedShipTo: string = "0";
  shipToControl = new FormControl("");
  shipToList: any[] = [];

  //BUSCADOR
  searchSubscription: Subscription;

  form: FormGroup;

  //Para filtros
  selectedStatuses: number[] = [];

  // Estados ERP
  selectedERPStatuses: number[] = [];

  selectedCustomerType: string[] = [];

  constructor(
    private readonly warehouseService: WarehouseService,
    private readonly subRouteService: SubRouteService,
    private readonly companyService: CompanyService,
    private orderCustomer: OrderCustomerService,
    private snackBar: MatSnackBar,
    private readonly cdr: ChangeDetectorRef,
    private readonly materialService: MaterialService,
    private ordersListService: OrdersListService,
    private readonly searchService: SearchService,
    private fb: FormBuilder
  ) {
    const today = new Date();
    this.form = this.fb.group({
      start: [today],
      end: [today],
      startTrip: [],
      endTrip: [],
      companyId: [],
      salesOfficeId: [],
      documentClassId: [],
      subRouteId: [],
      assignedUserId: [],
      payingBusinessPartnerId: [],
      receivingBusinessPartnerId: [],
      warehouseId: [],
      end_startTrip: [],
      end_endTrip: [],
      soldToControl: [],
      searchReceivingBusinessPartner: [],
      searchAssignedUser: [],
      materialGroupId: [[]],
      materialId: [],
    });
  }

  loadConfigData = false;
  loadDocumentsData = false;
  loadRoutesData = false;
  loadBusinessPartnersData = false;
  loadMaterialsData = false;

  onConfigExpanded(): void {
    this.loadConfigData = true;
  }

  onDocumentsExpanded(): void {
    this.loadDocumentsData = true;
  }

  onRoutesExpanded(): void {
    this.loadRoutesData = true;
  }

  onBusinessPartnersExpanded(): void {
    this.loadBusinessPartnersData = true;
  }

  onMaterialsExpanded(): void {
    this.loadMaterialsData = true;
  }

  ngOnInit(): void {
    this.onHandleSearch();
    this.fetchCompanies();
    this.onFilter();
    this.isInitialized = true;
  }

  ngAfterViewInit(): void {
    this.documentClassMultiFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroyDocumentClass))
      .subscribe(() => {
        this.filterDocumentClasses();
        this.setToggleAllCheckboxStateDocumentClass();
      });

    this.searchControl.valueChanges
      .pipe(
        skip(1),
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this._onDestroy)
      )
      .subscribe((filterValue: string) => {
        if (this.isInitialized) {
          this.pageIndex = 1;
          this.search = filterValue;
          this.onFilter();
        }
      });
    this.soldToControl.valueChanges
      .pipe(
        skip(1),
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this._onDestroy)
      )
      .subscribe((value) => {
        if (this.isInitialized) {
          if (
            typeof value === "string" &&
            value.trim().length === 24 &&
            /^[a-f0-9]{24}$/.test(value)
          ) {
            return;
          }
          this.soldToList = [];
          this.soldToFilterSelected = null;
          this.getListSoldTo(value);

          this.getListReport();
        }
      });

    this.shipToControl.valueChanges
      .pipe(
        skip(1),
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this._onDestroy)
      )
      .subscribe((value) => {
        if (this.isInitialized) {
          if (
            typeof value === "string" &&
            value.trim().length === 24 &&
            /^[a-f0-9]{24}$/.test(value)
          ) {
            return;
          }

          this.shipToList = [];
          this.getListShipTo(value);
          this.getListReport();
        }
      });

    this.setupMaterialSearchControl();
    this.getMaterialList();
  }

  public onHandleSearch() {
    this.searchSubscription = this.searchService.searchTerm$
      .pipe(skip(1), debounceTime(300))
      .subscribe((term) => {
        if (this.isInitialized) {
          this.pageIndex = 1;
          this.search = term;
          this.onFilter();
        }
      });
  }

  onFilter() {
    this.range.valueChanges.subscribe((rangeValues) => {
      this.filter.dateini = rangeValues.start || this.filter.dateini;
      this.filter.dateend = rangeValues.end || this.filter.dateend;
    });

    this.getListReport();
  }

  onClearFilter() {
    this.filter = {
      company: this.filter.company,
      dateini: new Date(),
      dateend: new Date(),
      warehouseId: null,
      routes: [],
      item: null,
    };
    this.range.reset({
      start: new Date(),
      end: new Date(),
    });
    const today = new Date();
    this.form.reset({ start: today, end: today });
    this.onFilter();
  }

  getListReport() {
    this.loading = true;
    let options = {
      currentPage: this.pageIndex,
      limitPerPage: this.pageSize,
      search: this.search,
      sort: this.getSortField(),
      sortDirection: this.sortDirection,
    };

    let filter: IFilters = {
      type: this.type,
      startDate: DateHelper.getStartOfDay(this.form.get("start")?.value),
      endDate:
        DateHelper.getEndOfDay(this.form.get("end")?.value) ??
        DateHelper.getEndOfDay(this.form.get("start")?.value) ??
        null,
      startTrip: DateHelper.getStartOfDay(this.form.get("startTrip")?.value),
      endTrip:
        DateHelper.getEndOfDay(this.form.get("endTrip")?.value) ??
        DateHelper.getEndOfDay(this.form.get("startTrip")?.value) ??
        null,
      end_startTrip: DateHelper.getStartOfDay(
        this.form.get("end_startTrip")?.value
      ),
      end_endTrip:
        DateHelper.getEndOfDay(this.form.get("end_endTrip")?.value) ??
        DateHelper.getEndOfDay(this.form.get("end_startTrip")?.value) ??
        null,
      companyId: this.form.get("companyId")?.value,
      salesOfficeId: this.form.get("salesOfficeId")?.value,
      warehouseId: this.form.get("warehouseId")?.value, //
      documentClass: this.form.get("documentClassId")?.value,
      subRouteId: this.form.get("subRouteId")?.value, //
      soldTo: this.form.get("payingBusinessPartnerId")?.value,
      shipTo: this.form.get("receivingBusinessPartnerId")?.value,
      seller: this.form.get("assignedUserId")?.value,
      status: this.selectedStatuses ? this.selectedStatuses : null,
      statusERP: this.selectedERPStatuses.length
        ? this.selectedERPStatuses
        : null,
      typeCustomer: this.selectedCustomerType.length
        ? this.selectedCustomerType
        : null,
      materialGroupId: this.form.get("materialGroupId").value,
      materialId: this.form.get("materialId").value,
      ordersToFilter: null,
    };

    this.orderCustomer.findAllWithCount(options, filter).subscribe({
      next: (res: IPaginationResult<OrderDetailAttributes>) => {
        this.data = res?.data?.[0]?.data ?? [];
        this.totalItems = res?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
        this.loading = false;
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error al obtener reporte",
          "Cerrar",
          { duration: 5000 }
        );
        this.loading = false;
      },
    });
  }

  // Smart Grid Event Handlers
  onPageChanged(event: PageChangedEvent): void {
    this.pageIndex = event.pageIndex + 1; // Convert 0-based to 1-based
    this.pageSize = event.pageSize;
    this.getListReport();
  }

  onSortChanged(event: SortChangedEvent<OrderDetailAttributes>): void {
    if (event.sortStates.length > 0) {
      const sortState = event.sortStates[0];
      this.sortActive = sortState.field as keyof OrderDetailAttributes;
      this.sortDirection = sortState.direction === "asc" ? "ASC" : "DESC";
    }
    this.getListReport();
  }

  onSearchChanged(event: SearchChangedEvent): void {
    this.search = event.searchTerm;
    this.pageIndex = 1;
    this.getListReport();
  }

  private getSortField(): keyof OrderDetailAttributes {
    const sortFieldMapping: Record<string, keyof OrderDetailAttributes> = {
      subRoute: <any>"subRoute.name",
      user: <any>"user",
      serials: <any>"serials",
      date: <any>"date",
      shipToExternalKeyCode: <any>"shipToExternalKeyCode",
      materialName: <any>"materialName.name",
      soldQuantity: "soldQuantity",
      baseUnitOfMeasure: "baseUnitOfMeasure",
      unitPrice: "unitPrice",
      subTotal: "subTotal",
      total: "total",
    };
    return sortFieldMapping[this.sortActive] || "total";
  }

  //filter
  private fetchCompanies(): void {
    this.companyService.findToComboBox().subscribe((res) => {
      if (res.data.length === 1) {
        this.UMPActive = res.data[0].UMPActive ?? false;
        this.currencyCode = res.data[0].currencyBase?.code || "";
        this.updatePriceHeaders();
      } else {
        this.UMPActive = false;
        this.currencyCode = "";
      }
    });
  }

  /**
   * Update price column headers with currency code
   * NOTE: Not needed anymore since CurrencyCellRendererComponent shows symbol in cells
   */
  private updatePriceHeaders(): void {
    // Currency symbols are now displayed in cells via CurrencyCellRendererComponent
    // Headers remain simple: "Precio", "Subtotal", "Total"
    // No need to update headers dynamically
  }

  private fetchAllWarehouses(): void {
    this.warehouseService.findToComboBox().subscribe({
      next: (res) => {
        this.warehouseList = res.data;
        this.warehouseFilterList = this.warehouseList.slice();
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error al obtener alamacenes.",
          "Cerrar",
          { duration: 5000 }
        );
      },
    });
  }

  changueOptionsWarehouse(event) {
    if (!this.warehouseList) {
      return;
    }
    let search = event;
    if (!search) {
      this.warehouseFilterList = this.warehouseList.slice();
      return;
    } else {
      search = search.toLowerCase();
    }
    this.warehouseFilterList = this.warehouseList.filter(
      (item) =>
        item.externalKeys?.code.toLowerCase().indexOf(search) > -1 ||
        item.name.toLowerCase().indexOf(search) > -1
    );
  }

  changeWarehouse(event): void {
    this.subRouteList = [];
    this.listFilterSubRouteList = [];
    this.getAllRutas();
  }

  private getAllRutas(): void {
    this.subRouteService
      .findToWarehouse(this.filter.warehouseId ? [this.filter.warehouseId] : [])
      .subscribe({
        next: (res) => {
          this.subRouteList = res.data;
          this.listFilterSubRouteList = this.subRouteList.slice();
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error al obtener rutas.",
            "Cerrar",
            { duration: 5000 }
          );
        },
      });
  }

  changueOptionsSubRoute(event) {
    if (!this.subRouteList) {
      return;
    }
    let search = event;
    if (!search) {
      this.listFilterSubRouteList = this.subRouteList.slice();
      return;
    } else {
      search = search.toLowerCase();
    }
    this.listFilterSubRouteList = this.subRouteList.filter(
      (item) =>
        item.externalKeys?.code.toLowerCase().indexOf(search) > -1 ||
        item.name.toLowerCase().indexOf(search) > -1
    );
  }

  onDocumentClassSelectionChange(selectedValues: string[]): void {
    this.selectedDocumentClass = selectedValues;
  }

  private getListSeller() {
    this.ordersListService.fetchMasterUsers().subscribe({
      next: (res: IRequestResult<MasterUserInterface[]>) => {
        if (res.status == "success") {
          this.sellerList = res.data;
        }
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error al obtener vendedores.",
          "Cerrar",
          { duration: 5000 }
        );
      },
    });
  }

  getListSoldTo(search?): void {
    let dataToSend = {
      companyIds: [environment.companyId],
      search: search?.toString() || "",
    };
    this.ordersListService.fetchSoldTos(dataToSend).subscribe((res) => {
      this.soldToList = res?.data;
      this.cdr.detectChanges();
    });
  }

  onChangeSoldTo() {
    this.soldToFilterSelected = this.soldToList.find(
      (stl) => stl.id == this.soldToControl.value
    );
    this.shipToControl.setValue("");
    this.getListShipTo();
  }

  getListShipTo(search?): void {
    this.shipToList = [];
    const soldToValue = this.soldToFilterSelected
      ? this.soldToFilterSelected._id
      : null;

    let dataToSend: any = {
      companyIds: [environment.companyId],
      search: search?.toString() || "",
    };

    if (soldToValue) {
      dataToSend.soldToMongoId = soldToValue;
    }

    this.ordersListService.fetchShipTos(dataToSend).subscribe((res) => {
      this.shipToList = res?.data;
      this.cdr.detectChanges();
    });
  }

  fetchShipToName = (shipToId: string): string => {
    const foundShipTo = this.shipToList.find(
      (shipTo) => shipTo.id === shipToId
    );
    if (foundShipTo) {
      return foundShipTo.name;
    }
    return "";
  };

  fetchSoldToName = (soldtoId: string): string => {
    const foundSoldTo = this.soldToList.find(
      (soldTo) => soldTo.id === soldtoId
    );
    if (foundSoldTo) {
      return foundSoldTo.name;
    }
    return "";
  };

  private setupMaterialSearchControl(): void {
    this.searchSubject
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((searchTerm) => {
        if (searchTerm) {
          this.searchMaterial = searchTerm;
          this.getMaterialList();
        }
      });
  }

  changueOptionsMaterial(event) {
    this.searchSubject.next(event);
  }

  private getMaterialList(): void {
    const materialOptions = {
      search: this.searchMaterial ? this.searchMaterial : null,
      currentPage: 1,
      limitPerPage: 50,
      sortActive: "name",
      //sortDirection: "ASC"
    };

    this.materialService
      .findAllWithCount(
        materialOptions,
        this.filter.company ? this.filter.company : []
      )
      .subscribe({
        next: (res: IPaginationResult<MaterialAttributes>) => {
          this.materialList = res?.data?.[0]?.data ?? [];
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error al traer registros",
            "Cerrar",
            { duration: 3000 }
          );
        },
      });
  }

  private fetchDocumentClass(): void {
    let dataToFilter = {
      type: this.type,
    };
    // this.ordersListService.fetchDocumentClasses(dataToFilter).subscribe((res) => {
    //   this.documentClassesList = res.data;
    // })
    this.ordersListService
      .fetchDocumentClasses(dataToFilter)
      .subscribe((res) => {
        this.documentClassesList = res.data;
        this.filteredDocumentClasses.next(this.documentClassesList);
      });
  }

  private filterDocumentClasses(): void {
    if (!this.documentClassesList) {
      return;
    }
    let search = this.documentClassMultiFilterCtrl.value;
    if (!search) {
      this.filteredDocumentClasses.next(this.documentClassesList);
      return;
    } else {
      search = search.toLowerCase();
    }
    const filtered = this.documentClassesList.filter((docClass) =>
      docClass.name.toLowerCase().includes(search)
    );
    this.filteredDocumentClasses.next(filtered);
  }

  private setToggleAllCheckboxStateDocumentClass(): void {
    const selectedCount = this.selectedDocumentClass.length;
    const totalCount = this.documentClassesList.length;

    this.isCheckedDocumentClass =
      selectedCount > 0 && selectedCount === totalCount;
    this.isIndeterminateDocumentClass =
      selectedCount > 0 && selectedCount < totalCount;
  }

  ngOnDestroy(): void {
    this._onDestroyDocumentClass.next();
    this._onDestroyDocumentClass.complete();
    this._onDestroy.next();
    this._onDestroy.complete();
    this.searchSubscription.unsubscribe();
  }
}
